import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, FileText, User, LogOut, Plus,
  Clock, CheckCircle, AlertCircle, MessageSquare,
  ShoppingBag, Package, Globe, Shield, Zap, X, ShoppingCart
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCart } from '../../contexts/CartContext';
import { supabase } from '../../lib/supabase';
import QuoteCard from '../../components/QuoteCard';
import { useNotification } from '../../contexts/NotificationContext';

const PortalDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, signOut } = useAuth();
  const { language } = useLanguage();
  const { cartItems, cartTotal, addToCart, removeFromCart, clearCart } = useCart();
  const { showModal } = useNotification();
  const [portalSearchQuery, setPortalSearchQuery] = useState('');
  const [quotes, setQuotes] = useState([]);
  const [userServices, setUserServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingServices, setLoadingServices] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate('/auth/login');
      return;
    }

    // Check for success parameter in URL
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'quote') {
      setShowSuccessMessage(true);
      // Remove the success param from URL
      window.history.replaceState({}, '', '/portal');
      // Hide message after 5 seconds
      setTimeout(() => setShowSuccessMessage(false), 5000);
    }

    loadQuotes();
    loadUserServices();
  }, [user, navigate]);

  const loadUserServices = async () => {
    try {
      const { data, error } = await supabase
        .from('user_services')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUserServices(data || []);
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setLoadingServices(false);
    }
  };

  const loadQuotes = async () => {
    try {
      const { data, error } = await supabase
        .from('quote_requests')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setQuotes(data || []);
    } catch (error) {
      console.error('Error loading quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleCheckout = async () => {
    if (cartItems.length === 0) return;

    try {
      const servicesToInsert = cartItems.map(item => ({
        user_id: user.id,
        service_name: item.name,
        service_type: item.type,
        status: 'active',
        price: item.price,
        currency: item.currency || 'USD',
        billing_cycle: item.type === 'domain' ? 'annually' : 'monthly',
        purchase_date: new Date().toISOString(),
        expiry_date: item.type === 'domain'
          ? new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
          : null,
        metadata: item.metadata || {}
      }));

      const { error } = await supabase.from('user_services').insert(servicesToInsert);
      if (error) throw error;

      clearCart();
      loadUserServices();
      setActiveTab('services');
      showModal(
        language === 'tr' ? 'Başarılı' : 'Success',
        language === 'tr' ? 'Satın alma başarılı!' : 'Purchase successful!',
        'success'
      );
    } catch (err) {
      console.error('Checkout error:', err);
      showModal(
        language === 'tr' ? 'Hata' : 'Error',
        language === 'tr' ? 'Satın alma sırasında bir hata oluştu. Lütfen teknik ekiple iletişime geçin.' : 'An error occurred during checkout. Please contact support.',
        'error'
      );
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      in_review: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      responded: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      closed: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
    };
    return colors[status] || colors.new;
  };

  const getStatusIcon = (status) => {
    const icons = {
      new: Clock,
      in_review: AlertCircle,
      responded: MessageSquare,
      closed: CheckCircle
    };
    return icons[status] || Clock;
  };

  const stats = [
    {
      label: language === 'tr' ? 'Aktif Hizmetler' : 'Active Services',
      value: userServices.filter(s => s.status === 'active').length,
      icon: Package,
      color: 'from-purple-500 to-pink-500'
    },
    {
      label: language === 'tr' ? 'Toplam Talepler' : 'Total Requests',
      value: quotes.length,
      icon: FileText,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      label: language === 'tr' ? 'Bekleyen Yanıt' : 'Pending Response',
      value: quotes.filter(q => q.status === 'responded').length,
      icon: MessageSquare,
      color: 'from-green-500 to-emerald-500'
    }
  ];

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-500 dark:text-gray-400 font-medium">
            {language === 'tr' ? 'Oturum kontrol ediliyor...' : 'Checking session...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {language === 'tr' ? 'Müşteri Portalı' : 'Customer Portal'}
            </h1>
            <button
              onClick={handleSignOut}
              className="inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {language === 'tr' ? 'Çıkış' : 'Sign Out'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Message */}
        {showSuccessMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl flex items-start"
          >
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-green-800 dark:text-green-300 mb-1">
                {language === 'tr' ? 'Talebiniz Alındı!' : 'Request Submitted!'}
              </p>
              <p className="text-sm text-green-700 dark:text-green-200">
                {language === 'tr'
                  ? 'Teklif talebiniz başarıyla gönderildi. Ekibimiz en kısa sürede size dönüş yapacaktır.'
                  : 'Your quote request has been submitted successfully. Our team will get back to you soon.'}
              </p>
            </div>
          </motion.div>
        )}

        {/* Tabs */}
        <div className="flex space-x-1 bg-gray-200 dark:bg-gray-800 p-1 rounded-xl mb-8 overflow-x-auto no-scrollbar">
          {[
            { id: 'dashboard', icon: LayoutDashboard, label: language === 'tr' ? 'Panel' : 'Dashboard' },
            { id: 'services', icon: Package, label: language === 'tr' ? 'Hizmetlerim' : 'My Services' },
            { id: 'store', icon: ShoppingBag, label: language === 'tr' ? 'Mağaza' : 'Store' },
            { id: 'quotes', icon: FileText, label: language === 'tr' ? 'Taleplerim' : 'My Requests' },
            { id: 'profile', icon: User, label: language === 'tr' ? 'Profil' : 'Profile' }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 inline-flex items-center justify-center px-4 py-3 rounded-lg font-medium transition-all whitespace-nowrap ${activeTab === tab.id
                  ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
              >
                <Icon className="w-5 h-5 mr-2" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <div className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 rounded-2xl p-8 text-white shadow-xl relative overflow-hidden">
              <div className="relative z-10">
                <h2 className="text-3xl font-bold mb-2">
                  {language === 'tr' ? `Hoş geldiniz, ${user?.email?.split('@')[0]}` : `Welcome, ${user?.email?.split('@')[0]}`}
                </h2>
                <p className="text-blue-100 mb-6 max-w-lg">
                  {language === 'tr'
                    ? 'Dijital varlıklarınızı tek panelden yönetin. Yeni bir hizmete ihtiyacınız mı var?'
                    : 'Manage your digital assets from a single panel. Need a new service?'}
                </p>
                <div className="flex flex-wrap gap-4">
                  <button
                    onClick={() => setActiveTab('store')}
                    className="inline-flex items-center px-6 py-3 bg-white text-blue-600 rounded-xl font-semibold hover:shadow-xl transition-all"
                  >
                    <ShoppingBag className="w-5 h-5 mr-2" />
                    {language === 'tr' ? 'Hizmet Satın Al' : 'Buy Service'}
                  </button>
                  <button
                    onClick={() => navigate('/portal/new-quote')}
                    className="inline-flex items-center px-6 py-3 bg-blue-500/20 backdrop-blur-md border border-white/30 text-white rounded-xl font-semibold hover:bg-blue-500/30 transition-all"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    {language === 'tr' ? 'Teklif Talebi' : 'Request Quote'}
                  </button>
                </div>
              </div>
              {/* Abstract decorative circles */}
              <div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-64 h-64 bg-blue-400/20 rounded-full blur-3xl"></div>
            </div>

            {/* Quick Actions Band */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                {
                  icon: Globe,
                  label: language === 'tr' ? 'Domain Sorgula' : 'Domain Search',
                  onClick: () => setActiveTab('store'),
                  color: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20'
                },
                {
                  icon: Shield,
                  label: language === 'tr' ? 'Marka Tescili' : 'Trademark',
                  onClick: () => setActiveTab('store'),
                  color: 'text-purple-600 bg-purple-50 dark:bg-purple-900/20'
                },
                {
                  icon: Zap,
                  label: language === 'tr' ? 'Hızlı Destek' : 'Quick Support',
                  onClick: () => navigate('/portal/new-quote'),
                  color: 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20'
                },
                {
                  icon: User,
                  label: language === 'tr' ? 'Profil Ayarları' : 'Settings',
                  onClick: () => setActiveTab('profile'),
                  color: 'text-gray-600 bg-gray-50 dark:bg-gray-800'
                }
              ].map((action, i) => (
                <button
                  key={i}
                  onClick={action.onClick}
                  className="flex flex-col items-center p-4 rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 hover:shadow-md transition-all group"
                >
                  <div className={`w-10 h-10 ${action.color} rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                    <action.icon className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{action.label}</span>
                </button>
              ))}
            </div>

            {/* Stats */}
            <div className="grid md:grid-cols-3 gap-6">
              {stats.map((stat, idx) => {
                const Icon = stat.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <span className="text-3xl font-bold text-gray-900 dark:text-white">{stat.value}</span>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 font-medium">{stat.label}</p>
                  </motion.div>
                );
              })}
            </div>

            {/* Recent Quotes */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  {language === 'tr' ? 'Son Talepler' : 'Recent Requests'}
                </h3>
                <button
                  onClick={() => setActiveTab('quotes')}
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm font-medium"
                >
                  {language === 'tr' ? 'Tümünü Gör' : 'View All'}
                </button>
              </div>

              {loading ? (
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-8 text-center">
                  <p className="text-gray-600 dark:text-gray-400">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</p>
                </div>
              ) : quotes.length === 0 ? (
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-8 text-center">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {language === 'tr' ? 'Henüz teklif talebiniz yok' : 'No quote requests yet'}
                  </p>
                  <button
                    onClick={() => navigate('/portal/new-quote')}
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-xl transition-all"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    {language === 'tr' ? 'İlk Talebinizi Oluşturun' : 'Create Your First Request'}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {quotes.slice(0, 3).map((quote) => (
                    <QuoteCard
                      key={quote.id}
                      quote={quote}
                      language={language}
                      isAdmin={false}
                    />
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Quotes Tab */}
        {activeTab === 'quotes' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {language === 'tr' ? 'Tüm Taleplerim' : 'All My Requests'}
              </h2>
              <button
                onClick={() => navigate('/portal/new-quote')}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-xl transition-all"
              >
                <Plus className="w-5 h-5 mr-2" />
                {language === 'tr' ? 'Yeni Talep' : 'New Request'}
              </button>
            </div>

            {quotes.length === 0 ? (
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-12 text-center">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">
                  {language === 'tr' ? 'Henüz teklif talebiniz yok' : 'No quote requests yet'}
                </p>
              </div>
            ) : (
              <div className="grid gap-4">
                {quotes.map((quote) => (
                  <QuoteCard
                    key={quote.id}
                    quote={quote}
                    language={language}
                    isAdmin={false}
                  />
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {language === 'tr' ? 'Aktif Hizmetlerim' : 'My Active Services'}
              </h2>
            </div>

            <div className="grid gap-6">
              {loadingServices ? (
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-12 text-center">
                  <p className="text-gray-600 dark:text-gray-400">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</p>
                </div>
              ) : userServices.length === 0 ? (
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-12 text-center">
                  <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {language === 'tr' ? 'Henüz aktif bir hizmetiniz bulunmuyor.' : 'You have no active services yet.'}
                  </p>
                  <button
                    onClick={() => setActiveTab('store')}
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-xl transition-all"
                  >
                    <ShoppingBag className="w-5 h-5 mr-2" />
                    {language === 'tr' ? 'Hizmetleri İncele' : 'Browse Services'}
                  </button>
                </div>
              ) : (
                userServices.map((service) => (
                  <div key={service.id} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden">
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-xl flex items-center justify-center">
                            {service.service_type === 'domain' ? <Globe className="w-6 h-6" /> : <Package className="w-6 h-6" />}
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">{service.service_name}</h3>
                            <p className="text-sm text-gray-500 capitalize">{service.service_type}</p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 text-xs font-bold rounded-full uppercase ${service.status === 'active'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                          }`}>
                          {service.status}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-4 border-y border-gray-100 dark:border-gray-800">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">{language === 'tr' ? 'Kayıt Tarihi' : 'Reg. Date'}</p>
                          <p className="text-sm font-semibold dark:text-white">
                            {new Date(service.purchase_date).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US')}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">{language === 'tr' ? 'Yenileme' : 'Renewal'}</p>
                          <p className="text-sm font-semibold dark:text-white">
                            {service.expiry_date ? new Date(service.expiry_date).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US') : '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">{language === 'tr' ? 'Dönem' : 'Billing'}</p>
                          <p className="text-sm font-semibold dark:text-white capitalize">{service.billing_cycle}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">{language === 'tr' ? 'Ücret' : 'Price'}</p>
                          <p className="text-sm font-semibold dark:text-white">
                            {service.price} {service.currency}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-800/50 px-6 py-4 flex justify-end gap-3">
                      {service.service_type === 'domain' && (
                        <button className="text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
                          {language === 'tr' ? 'DNS Yönetimi' : 'DNS Management'}
                        </button>
                      )}
                      <button className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline">
                        {language === 'tr' ? 'Detaylar' : 'Details'}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}

        {/* Store Tab */}
        {activeTab === 'store' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {language === 'tr' ? 'Dijital Mağaza' : 'Digital Store'}
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                {language === 'tr' ? 'İşinizi büyütmek için ihtiyacınız olan tüm araçlar.' : 'All the tools you need to grow your business.'}
              </p>
            </div>

            {/* Quick Domain Search in Store */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-3xl p-6 shadow-sm">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-blue-600" />
                {language === 'tr' ? 'Hemen Domain Sorgula' : 'Quick Domain Search'}
              </h3>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (portalSearchQuery.trim()) {
                    navigate(`/domain-search?q=${encodeURIComponent(portalSearchQuery.trim())}`);
                  }
                }}
                className="flex flex-col sm:flex-row gap-3"
              >
                <input
                  type="text"
                  value={portalSearchQuery}
                  onChange={(e) => setPortalSearchQuery(e.target.value)}
                  placeholder="example.com"
                  className="flex-1 px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none dark:text-white"
                />
                <button
                  type="submit"
                  className="px-8 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors"
                >
                  {language === 'tr' ? 'Sorgula' : 'Search'}
                </button>
              </form>
            </div>

            {/* Cart Summary Section */}
            {cartItems.length > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-blue-600 rounded-3xl p-8 text-white shadow-xl shadow-blue-500/20"
              >
                <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                  <div>
                    <h3 className="text-2xl font-bold mb-2 flex items-center gap-2">
                      <ShoppingCart className="w-6 h-6" />
                      {language === 'tr' ? 'Sepetiniz' : 'Your Cart'}
                    </h3>
                    <p className="text-blue-100 italic">
                      {cartItems.length} {language === 'tr' ? 'ürün hazır.' : 'items ready.'}
                    </p>
                  </div>

                  <div className="flex-1 w-full max-w-md bg-white/10 backdrop-blur-md rounded-2xl p-4">
                    <div className="space-y-2 mb-4">
                      {cartItems.map((item) => (
                        <div key={item.id} className="flex justify-between items-center text-sm border-b border-white/10 pb-2">
                          <span className="font-medium truncate max-w-[200px]">{item.name}</span>
                          <div className="flex items-center gap-3">
                            <span className="font-bold">${item.price}</span>
                            <button
                              onClick={() => removeFromCart(item.id)}
                              className="text-white/50 hover:text-white transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between items-center font-bold text-lg">
                      <span>{language === 'tr' ? 'Toplam' : 'Total'}</span>
                      <span>${cartTotal.toFixed(2)}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate('/basket')}
                    className="w-full md:w-auto px-8 py-4 bg-white text-blue-600 rounded-2xl font-bold hover:shadow-2xl transition-all hover:-translate-y-1"
                  >
                    {language === 'tr' ? 'Sepete Git' : 'View Basket'}
                  </button>
                </div>
              </motion.div>
            )}

            {/* Service Categories */}
            <div className="grid md:grid-cols-2 gap-8">


              {/* Category: Consulting */}
              <div className="space-y-4">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  {language === 'tr' ? 'Danışmanlık ve Yönetim' : 'Consulting & Management'}
                </h3>
                <div className="grid gap-4">
                  {[
                    { id: 'service-amazon', title: 'Amazon Danışmanlığı', price: 499, period: 'mo', icon: ShoppingBag, type: 'consulting' },
                    { id: 'service-etsy', title: 'Etsy Mağaza Yönetimi', price: 399, period: 'mo', icon: ShoppingBag, type: 'management' },
                    { id: 'service-social', title: 'Sosyal Medya Yönetimi', price: 299, period: 'mo', icon: ShoppingBag, type: 'social' }
                  ].map((item, i) => (
                    <div
                      key={i}
                      onClick={() => addToCart({
                        id: item.id,
                        name: item.title,
                        type: item.type,
                        price: item.price,
                        currency: 'USD'
                      })}
                      className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 p-4 rounded-2xl flex items-center justify-between hover:border-blue-200 dark:hover:border-blue-900 transition-colors cursor-pointer group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-gray-50 dark:bg-gray-800 rounded-xl flex items-center justify-center group-hover:bg-blue-50 dark:group-hover:bg-blue-900/20 transition-colors">
                          <item.icon className="w-5 h-5 text-gray-400 group-hover:text-blue-600" />
                        </div>
                        <div>
                          <p className="font-bold dark:text-white">{item.title}</p>
                          <p className="text-sm text-gray-500">${item.price}/{item.period}</p>
                        </div>
                      </div>
                      <Plus className="w-5 h-5 text-gray-300 group-hover:text-blue-600" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Category: Infrastructure */}
              <div className="space-y-4">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-500" />
                  {language === 'tr' ? 'Altyapı Hizmetleri' : 'Infrastructure'}
                </h3>
                <div className="grid gap-4">
                  {[
                    { id: 'service-hosting', title: 'Cloud Hosting (Pro)', price: 19.99, period: 'mo', icon: Zap, type: 'hosting' },
                    { id: 'service-ssl', title: 'SSL Sertifikası (EV)', price: 99, period: 'yr', icon: Shield, type: 'security' },
                    { id: 'service-trademark', title: 'Marka Tescil Takibi', price: 149, period: 'one-time', icon: FileText, type: 'legal' }
                  ].map((item, i) => (
                    <div
                      key={i}
                      onClick={() => addToCart({
                        id: item.id,
                        name: item.title,
                        type: item.type,
                        price: item.price,
                        currency: 'USD'
                      })}
                      className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 p-4 rounded-2xl flex items-center justify-between hover:border-purple-200 dark:hover:border-purple-900 transition-colors cursor-pointer group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-gray-50 dark:bg-gray-800 rounded-xl flex items-center justify-center group-hover:bg-purple-50 dark:group-hover:bg-purple-900/20 transition-colors">
                          <item.icon className="w-5 h-5 text-gray-400 group-hover:text-purple-600" />
                        </div>
                        <div>
                          <p className="font-bold dark:text-white">{item.title}</p>
                          <p className="text-sm text-gray-500">${item.price}/{item.period}</p>
                        </div>
                      </div>
                      <Plus className="w-5 h-5 text-gray-300 group-hover:text-purple-600" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              {language === 'tr' ? 'Profil Bilgilerim' : 'My Profile'}
            </h2>
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-8">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {language === 'tr' ? 'E-posta' : 'Email'}
                  </label>
                  <input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white"
                  />
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {language === 'tr'
                    ? 'Profil bilgilerinizi güncellemek için destek@cplus.com.tr adresine e-posta gönderin.'
                    : 'To update your profile information, email support@cplus.com.tr'}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PortalDashboard;
